import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  signal,
  computed,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormControl,
} from '@angular/forms';
import { AppCommonModule } from '@app/components/app.common.module';
import { TranslateModule } from '@ngx-translate/core';
import {
  additionalCollateralTaxTypes,
  companyTypesFinancialSetup,
  financialSetupAppliesSofomRules,
  FINANCIAL_SETUP_COMPANY_BOTH,
  currencies,
  downPaymentTaxType,
  FinancialProductEnum,
  financialProductsList,
  openingFeeType,
  residualValueTypes,
  subsidyTaxTypes,
  TaxType,
  taxTypes,
  warrantyDepositType,
  warrantyTypes,
  balloonTypes,
} from '@app/pages/quote/open-quote/model/open-quote.model';
import { taxFreeOptionsNew } from '@app/pages/quote/open-quote/deals/deal-form/model';
import { FinancialSetupCreationDto, FinancialCondition } from '@app/pages/quote/standard-quote/model/financial-setup.model';
import { environment } from '@env/environment.local';

import { TippyDirective } from '@ngneat/helipopper';
import { BaseComponent } from '@app/components/base/base.component';
import { interval } from 'rxjs';

@Component({
  selector: 'app-general-information-form',
  standalone: true,
  templateUrl: './general-information-form.component.html',
  styleUrls: ['./general-information-form.component.css'],

  imports: [FormsModule, AppCommonModule, CommonModule, TranslateModule, TippyDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GeneralInformationFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GeneralInformationFormComponent),
      multi: true,
    },
  ],
})
export class GeneralInformationFormComponent
  extends BaseComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  form: FormGroup;
  public taxTypes = taxTypes;
  public downPaymentTaxType = downPaymentTaxType;
  public warrantyDepositType = warrantyDepositType;
  public residualValueTypes = residualValueTypes;
  public warrantyTypes = warrantyTypes;
  public additionalCollateralTaxTypes = additionalCollateralTaxTypes;
  public subsidyTaxTypes = subsidyTaxTypes;
  public balloonTaxTypes = balloonTypes;
  public openingFeeType = openingFeeType;
  public currencies = currencies;
  public financialProductsListFull = signal(financialProductsList);
  public taxFreeOptions = taxFreeOptionsNew;
  readonly featureLoyaltyFeriaQuotation = environment.featureLoyaltyFeriaQuotation;
  public companyName = signal('');
  public financialProductsFiltered = computed(() => {
    return this.financialProductsListFull().filter(product => {
      if (financialSetupAppliesSofomRules(this.companyName())) {
        return product.value !== FinancialProductEnum.pureLease;
      }
      if (product.value === FinancialProductEnum.capLoan) return false;
      if (product.value === FinancialProductEnum.realEstate) return false;
      return true;
    });
  });
  public companyTypes = companyTypesFinancialSetup;
  changeRequestOpenModal: number = 0;
  isEditing: boolean = false;
  module = '';

  readonly showFinancialSetupQuoteValidityDays =
    environment.showFinancialSetupQuoteValidityDays;

  filterModels: any = {};

  @Input() financialSetupCreationDto: FinancialSetupCreationDto;
  @Input() isReadyToEdit: boolean;
  private _financialConditions: FinancialCondition[] = [];
  
  @Input() 
  set financialConditions(value: FinancialCondition[]) {
    const newValue = value || [];
    const currentSignature = this._financialConditions.map(c => `${c.term}:${c.gracePeriod || 0}`).sort().join('|');
    const newSignature = newValue.map(c => `${c.term}:${c.gracePeriod || 0}`).sort().join('|');
    
    if (currentSignature !== newSignature || this._financialConditions.length !== newValue.length) {
      this._financialConditions = newValue;
      if (this.form) {
        this.setPreferencesValidator();
      }
    }
  }
  
  get financialConditions(): FinancialCondition[] {
    return this._financialConditions;
  }

  @Output() financialSetupCreationChanges = new EventEmitter<FinancialSetupCreationDto>();
  @Output() requestOpenModal: EventEmitter<void> = new EventEmitter<void>();
  @Output() classificationChange = new EventEmitter<boolean>();

  constructor(private fb: FormBuilder) {
    super();
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only rebuild from DTO when it actually changes. Rebuilding on
    // financialConditions updates wiped allowCapitalGrace / allowInterestGrace.
    if (changes['financialSetupCreationDto'] && this.financialSetupCreationDto) {
      this.createFormToUpdate();
      this.updateFieldState();
      this.bindPreferenceChangeHandlers();
    }
    if (this.form) {
      this.setPreferencesValidator();
    }
  }

  onChangeFinancialProduct(event: any) {
    const isPureLeaseSelected = event.target?.value === FinancialProductEnum.pureLease;
    this.companyTypes = companyTypesFinancialSetup.filter(company =>
      isPureLeaseSelected
        ? company.value !== 'SOFOM' && company.value !== FINANCIAL_SETUP_COMPANY_BOTH
        : true
    );
    this.form.controls['companyName'].setValue(isPureLeaseSelected ? 'DTFS' : '');
    if (isPureLeaseSelected) {
      this.companyName.set('DTFS');
    }
  }

  onChangeCompany(event: any) {
    // const isSOFOMSelected = event.target?.value === 'SOFOM';
    this.companyName.set(event.target?.value);
  }

  isRejected() {
    if (!this.financialSetupCreationDto) return false;
    return 'REJECTED' === this.financialSetupCreationDto?.status;
  }

  isApproved() {
    if (!this.financialSetupCreationDto) return false;
    return 'APPROVED' === this.financialSetupCreationDto?.status;
  }

  isDraft() {
    if (!this.financialSetupCreationDto) return false;
    return 'DRAFT' === this.financialSetupCreationDto?.status;
  }

  ngOnInit() {
    this.module = this.sendDataToMenu();
    if (this.isReadyToEdit) {
      this.isEditing = true;
      this.createFormToUpdate();
    } else {
      this.isEditing = false;
      this.createForm();
    }

    this.form.controls['financialProduct'].valueChanges.subscribe(value => {
      if (value === FinancialProductEnum.directCredit) {
        this.form.controls['downPaymentTaxType'].setValue(TaxType.invoiceWithTax);
      }
      if (value === FinancialProductEnum.financialLeasing) {
        this.form.controls['downPaymentTaxType'].setValue(TaxType.invoiceWithoutTax);
      }
      if (value === FinancialProductEnum.directCreditGlobe) {
        this.form.controls['downPaymentTaxType'].setValue(TaxType.invoiceWithTax);
      }
    });
    this.form.controls['financialProduct'].valueChanges.subscribe(value => {
      this.setConditions();
    });
    this.form.controls['classification'].valueChanges.subscribe((value: boolean) => {
      this.classificationChange.emit(value);
    });
    this.setConditions();
    this.updateFieldState();
    if (this.isEditing) {
      this.updateFieldState();
    }
    this.setPreferencesValidator();
    this.bindPreferenceChangeHandlers();
  }

  private bindPreferenceChangeHandlers() {
    if (!this.form) return;
    this.form.controls['allowCapitalGrace'].valueChanges.subscribe(value => {
      this.syncPreferenceToDto('allowCapitalGrace', value);
      this.setPreferencesValidator();
    });
    this.form.controls['allowInterestGrace'].valueChanges.subscribe(value => {
      this.syncPreferenceToDto('allowInterestGrace', value);
      this.setPreferencesValidator();
    });
  }

  private syncPreferenceToDto(
    key: 'allowCapitalGrace' | 'allowInterestGrace',
    value: boolean
  ) {
    if (this.financialSetupCreationDto) {
      this.financialSetupCreationDto[key] = !!value;
    }
  }

  public setConditions() {
    if (this.isFinancialLeasing()) {
      this.form.controls['purchaseOptionType'].setValidators([Validators.required]);
      this.form.controls['purchaseOptionType'].setValue(TaxType.invoiceWithTax);
      this.form.controls['purchaseOptionType'].updateValueAndValidity();
    } else {
      this.form.controls['purchaseOptionType'].clearValidators();
      this.form.controls['purchaseOptionType'].updateValueAndValidity();
    }

    if (this.iPureLease()) {
      this.form.controls['warrantyType'].setValidators([Validators.required]);
      this.form.controls['warrantyType'].updateValueAndValidity();
      this.form.controls['residualValueType'].setValidators([Validators.required]);
      this.form.controls['residualValueType'].updateValueAndValidity();
    } else {
      this.form.controls['warrantyType'].clearValidators();
      this.form.controls['warrantyType'].updateValueAndValidity();
      this.form.controls['residualValueType'].clearValidators();
      this.form.controls['residualValueType'].updateValueAndValidity();
    }
  }

  showPercentageInput(type: TaxType) {
    if (!type) return false;
    return ![TaxType.fixedAmount, TaxType.NOT_TAXED].some(t => t === type);
  }

  showInput(type: TaxType) {
    return type === TaxType.fixedAmount;
  }

  createForm() {
    if (!this.fb) return;
    // Inicializar companyName signal si existe en el DTO
    if (this.financialSetupCreationDto?.companyName) {
      this.companyName.set(this.financialSetupCreationDto.companyName);
    }
    this.form = this.fb.group({
      financialSetupName: new FormControl(null, [Validators.required]),
      financialSetupDescription: new FormControl(null, [Validators.required]),
      active: new FormControl(true, [Validators.required]),
      financialProduct: new FormControl(null, [Validators.required]),
      currency: new FormControl(null, [Validators.required]),
      classification: [null, [Validators.required]],
      companyName: new FormControl(true, [Validators.required]),
      paymentType: [null, [Validators.required]],
      paymentFormationType: [null, [Validators.required]],
      downPaymentTaxType: [null, []],
      warrantyDeposit: [TaxType.numberOfRents, []],
      openingFeeType: [TaxType.amountToFinance, []],
      //   additionalCollateral: [null, []],
      subsidyType: [null, [Validators.required]],
      subsidyValue: [null, []],
      balloonType: [null, []],
      balloonPercentage: [null, []],
      // allowPaymentDay: [false, []],

      allowCapitalGrace: [false, []],
      allowInterestGrace: [false, []],
      // allowOpeningFee: [false, []],
      warrantyType: [null, []],
      residualValueType: [null, []],
      purchaseOptionType: [TaxType.invoiceWithTax, []],
      taxFree: [false, []],
      ...(environment.showFinancialSetupQuoteValidityDays
        ? { quoteValidityDays: [30, [Validators.required]] }
        : {}),
      ...(environment.featureLoyaltyFeriaQuotation
        ? {
            loyaltyFinancialPackage: [false, []],
          }
        : {}),
    });
    this.subscribeFormValueChanges();
  }

  createFormToUpdate() {
    if (!this.fb || !this.form) return;
    const activeStartDate = this.financialSetupCreationDto.activeStartDate
      ? new Date(this.financialSetupCreationDto.activeStartDate)
      : null;
    const activeEndDate = this.financialSetupCreationDto.activeEndDate
      ? new Date(this.financialSetupCreationDto.activeEndDate)
      : null;

    // Inicializar companyName signal con el valor del DTO
    if (this.financialSetupCreationDto?.companyName) {
      this.companyName.set(this.financialSetupCreationDto.companyName);
    }

    this.form = this.fb.group({
      financialSetupName: new FormControl(this.financialSetupCreationDto.financialSetupName, [
        Validators.required,
      ]),
      financialSetupDescription: new FormControl(
        this.financialSetupCreationDto.financialSetupDescription,
        [Validators.required]
      ),
      active: new FormControl(this.financialSetupCreationDto.active, [Validators.required]),
      financialProduct: new FormControl(this.financialSetupCreationDto.financialProduct, [
        Validators.required,
      ]),
      currency: new FormControl(this.financialSetupCreationDto.currency, [Validators.required]),
      classification: new FormControl(this.financialSetupCreationDto.classification, [
        Validators.required,
      ]),
      companyName: new FormControl(this.financialSetupCreationDto.companyName, [
        Validators.required,
      ]),
      paymentType: [this.financialSetupCreationDto.paymentType, [Validators.required]],
      paymentFormationType: [
        this.financialSetupCreationDto.paymentFormationType,
        [Validators.required],
      ],
      downPaymentTaxType: [this.financialSetupCreationDto.downPaymentTaxType, []],
      warrantyDeposit: [this.financialSetupCreationDto.warrantyDeposit, []],
      openingFeeType: [this.financialSetupCreationDto.openingFeeType, []],
      subsidyType: [this.financialSetupCreationDto.subsidyType, [Validators.required]],
      subsidyValue: [
        this.financialSetupCreationDto.subsidyType === TaxType.NOT_TAXED
          ? null
          : this.financialSetupCreationDto.subsidyValue,
        [],
      ],
      balloonType: [this.financialSetupCreationDto.balloonType, []],
      balloonPercentage: [this.financialSetupCreationDto.balloonPercentage, []],
      allowCapitalGrace: [this.financialSetupCreationDto.allowCapitalGrace, []],
      allowInterestGrace: [this.financialSetupCreationDto.allowInterestGrace, []],
      warrantyType: [this.financialSetupCreationDto.warrantyType, []],
      residualValueType: [this.financialSetupCreationDto.residualValueType, []],
      purchaseOptionType: [this.financialSetupCreationDto.purchaseOptionType || TaxType.invoiceWithTax, []],
      taxFree: [this.financialSetupCreationDto.taxFree ?? false, []],
      ...(environment.showFinancialSetupQuoteValidityDays
        ? {
            quoteValidityDays: [
              this.financialSetupCreationDto.quoteValidityDays ?? 30,
              [Validators.required],
            ],
          }
        : {}),
      ...(environment.featureLoyaltyFeriaQuotation
        ? {
            loyaltyFinancialPackage: [
              Boolean(this.financialSetupCreationDto.loyaltyFinancialPackage),
              [],
            ],
          }
        : {}),
    });
    this.subscribeFormValueChanges();
    this.daimlerWarrantyBusinessLogic();
  }

  private subscribeFormValueChanges(): void {
    this.form.valueChanges.subscribe(() => {
      // getRawValue: companyName stays editable while other fields may be disabled
      const value = this.form.getRawValue();
      if (value && typeof value === 'object' && Object.keys(value).length > 0) {
        if (value.taxFree !== undefined) {
          value.taxFree =
            value.taxFree === true ||
            value.taxFree === 'true' ||
            value.taxFree === 'TRUE';
        }
        if (environment.featureLoyaltyFeriaQuotation) {
          value.loyaltyFinancialPackage =
            value.loyaltyFinancialPackage === true ||
            value.loyaltyFinancialPackage === 'true' ||
            value.loyaltyFinancialPackage === 'TRUE';
        }
        this.onChange(value);
        this.onTouched();
      }
    });
  }

  //#region CVA
  writeValue(value: any): void {
    if (value && this.form && Object.keys(value).length > 0) {
      // Solo actualizar si el valor no está vacío y tiene propiedades
      this.form.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private onChange = (_: any) => {};
  private onTouched = () => {};

  //#endregion

  //#region Validator
  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    return this.form.valid ? null : { invalidFormCustomer: true };
  }

  getInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  isCreditDirect() {
    return this.form.controls['financialProduct'].value === FinancialProductEnum.directCredit;
  }

  iPureLease() {
    return this.form.controls['financialProduct'].value === FinancialProductEnum.pureLease;
  }

  isFinancialLeasing() {
    return this.form.controls['financialProduct'].value === FinancialProductEnum.financialLeasing;
  }

  isDCBalloon() {
    return this.form.controls['financialProduct'].value === FinancialProductEnum.directCreditGlobe;
  }

  getCommentToRejected() {
    const histories = this.financialSetupCreationDto?.financialSetupHistories ?? [];
    const rejected = histories.filter((h: { action: string }) => h.action === 'REJECTED');
    const latest = rejected.length > 0 ? rejected[rejected.length - 1] : null;
    return latest?.comments?.trim() ?? null;
  }

  onChangeSubsidyType() {
    const subsidyTypeControl = this.form.controls['subsidyType'];
    const subsidyValueControl = this.form.controls['subsidyValue'];
    const value = subsidyTypeControl.value;
    if (!value) return;
    subsidyValueControl.setValue(null);
    if (value === TaxType.NOT_TAXED) {
      subsidyValueControl.clearValidators();
    } else {
      subsidyValueControl.setValidators([Validators.required]);
    }
    subsidyValueControl.updateValueAndValidity();
  }

  onChangeBalloonType() {
    const balloonTypeControl = this.form.controls['balloonType'];
    const balloonPercentageControl = this.form.controls['balloonPercentage'];
    const value = balloonTypeControl.value;
    if (!value) return;
    balloonPercentageControl.setValue(null);
    if (value === TaxType.NOT_TAXED) {
      balloonPercentageControl.clearValidators();
    } else {
      balloonPercentageControl.setValidators([Validators.required]);
    }
    balloonPercentageControl.updateValueAndValidity();
  }

  onInputFocus() {
    this.requestOpenModal.emit();
    this.changeRequestOpenModal = 1;
  }

  shouldDisableFields(): boolean {
    if (!this.isEditing) {
      return false;
    }

    const version = this.financialSetupCreationDto?.version;
    const status = this.financialSetupCreationDto?.status;
    if (version == '1' && (this.isDraft() || this.isRejected())) {
      return false;
    }
    return true;
  }

  updateFieldState(): void {
    const disable = this.shouldDisableFields();

    if (disable) {
      this.form.get('financialProduct')?.disable();
      this.form.get('currency')?.disable();
      this.form.get('classification')?.disable();
    } else {
      this.form.get('financialProduct')?.enable();
      this.form.get('currency')?.enable();
      this.form.get('classification')?.enable();
    }
    // Compañía editable en todos los estados
    this.form.get('companyName')?.enable();
  }

  onClassificationChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value === 'true';
    this.classificationChange.emit(value);
  }

  onWarrantyTypeChange(): void {
    const warrantyType = this.form.get('warrantyType')?.value;
    const residualValueTypeControl = this.form.controls['residualValueType'];

    if (warrantyType === 'DAIMLER') {
      // Para Garantía Daimler: establecer valor fijo calculado
      residualValueTypeControl?.setValue(TaxType.fixedAmount);
      residualValueTypeControl?.disable();
    } else if (warrantyType === 'EXTERNAL') {
      // Para Garantía de Terceros: habilitar el dropdown
      residualValueTypeControl?.enable();
      // Limpiar el valor para que el usuario seleccione
      residualValueTypeControl?.setValue(TaxType.invoiceWithTax);
    }
  }

  daimlerWarrantyBusinessLogic() {
    const warrantyType = this.form.get('warrantyType')?.value;
    const residualValueTypeControl = this.form.controls['residualValueType'];

    if (warrantyType === 'DAIMLER' && residualValueTypeControl.value !== TaxType.fixedAmount) {
      residualValueTypeControl?.setValue(TaxType.fixedAmount);
      residualValueTypeControl?.disable();
    }
  }

  /**
   * Validates that at least one preference checkbox is checked when grace periods exist
   */
  setPreferencesValidator() {
    const hasGracePeriods = this.checkIfGracePeriodsExist();
    
    if (hasGracePeriods) {
      const allowCapitalGraceControl = this.form.controls['allowCapitalGrace'];
      const allowInterestGraceControl = this.form.controls['allowInterestGrace'];
      
      // Add custom validator that checks both controls
      const validator = (control: AbstractControl): ValidationErrors | null => {
        const allowCapitalGrace = this.form.get('allowCapitalGrace')?.value || false;
        const allowInterestGrace = this.form.get('allowInterestGrace')?.value || false;
        
        if (!allowCapitalGrace && !allowInterestGrace) {
          return { preferencesRequired: true };
        }
        return null;
      };
      
      // Apply validator to both controls (using cross-field validation)
      allowCapitalGraceControl.setValidators([validator]);
      allowInterestGraceControl.setValidators([validator]);
      allowCapitalGraceControl.updateValueAndValidity({ emitEvent: false });
      allowInterestGraceControl.updateValueAndValidity({ emitEvent: false });
    } else {
      // Remove validators when no grace periods exist
      this.form.controls['allowCapitalGrace'].clearValidators();
      this.form.controls['allowInterestGrace'].clearValidators();
      this.form.controls['allowCapitalGrace'].updateValueAndValidity({ emitEvent: false });
      this.form.controls['allowInterestGrace'].updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Checks if any financial condition has a grace period > 0
   */
  checkIfGracePeriodsExist(): boolean {
    if (!this.financialConditions || this.financialConditions.length === 0) {
      return false;
    }
    return this.financialConditions.some(condition => 
      condition.gracePeriod && condition.gracePeriod > 0
    );
  }

  /**
   * Checks if preferences validation error exists
   */
  hasPreferencesError(): boolean {
    const allowCapitalGraceControl = this.form.controls['allowCapitalGrace'];
    const allowInterestGraceControl = this.form.controls['allowInterestGrace'];
    return (allowCapitalGraceControl.hasError('preferencesRequired') || 
            allowInterestGraceControl.hasError('preferencesRequired')) &&
           this.checkIfGracePeriodsExist();
  }
}
